import { Link } from 'react-router-dom';
import { ChevronRight, Cookie, FileText, ScrollText, Shield } from 'lucide-react';
import { LegalPageLayout } from '@/components/layout/LegalPageLayout';
import {
  LEGAL_SUITE_LAST_UPDATED_LABEL,
  LEGAL_SUITE_VERSION,
  AI_ASSISTANT_CONSENT_VERSION,
} from '@/constants/legalSuite';

const DOCUMENT_LINKS: {
  to: string;
  title: string;
  description: string;
  icon: typeof Shield;
}[] = [
  {
    to: '/privacy-policy',
    title: 'Política de privacidad',
    description: 'Qué datos tratamos, con qué finalidad, con quién y cuáles son tus derechos.',
    icon: Shield,
  },
  {
    to: '/cookies-policy',
    title: 'Política de cookies',
    description: 'Tipos de cookies y tecnologías similares, y cómo gestionarlas.',
    icon: Cookie,
  },
  {
    to: '/terms-and-conditions',
    title: 'Términos y condiciones',
    description: 'Uso del servicio, limitaciones (incluida la IA) y relación contractual.',
    icon: ScrollText,
  },
  {
    to: '/consent-information',
    title: 'Información sobre consentimiento',
    description: 'Texto del consentimiento unificado al registrarte y cómo queda registrado.',
    icon: FileText,
  },
];

export default function PrivacyCenter() {
  return (
    <LegalPageLayout
      title="Centro de privacidad"
      lastUpdated={LEGAL_SUITE_LAST_UPDATED_LABEL}
      version={LEGAL_SUITE_VERSION}
    >
      <p>
        Aquí tienes un solo lugar desde el que acceder a la información sobre protección de datos,
        cookies y condiciones de uso de FOCO (Tu Dinero, Tu Poder). Los textos completos están
        versionados; la versión actual del paquete legal es la que ves en la cabecera de esta página.
      </p>

      <div className="not-prose space-y-3 my-8">
        {DOCUMENT_LINKS.map(({ to, title, description, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className="flex items-center gap-4 rounded-lg border bg-card p-4 shadow-sm transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Icon className="h-5 w-5 text-primary" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <span className="font-semibold text-foreground">{title}</span>
              <p className="text-sm text-muted-foreground mt-0.5 leading-snug">{description}</p>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          </Link>
        ))}
      </div>

      <h2>Preferencias de cookies y datos en tu cuenta</h2>
      <p>
        Si ya tienes sesión iniciada, puedes revisar o ajustar preferencias relacionadas con privacidad
        y cookies en <strong>Configuración</strong> (sección de privacidad). Si entras sin cuenta,
        el navegador te pedirá consentimiento mediante el banner de cookies cuando corresponda.
      </p>
      <p className="not-prose">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 text-primary font-medium underline underline-offset-4 hover:text-primary/80"
        >
          Ir a Configuración
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      </p>

      <h2>Asistente con inteligencia artificial</h2>
      <p>
        El uso del asistente financiero con IA requiere un{' '}
        <strong>consentimiento adicional</strong> (distinto del del registro). Se te muestra en un
        modal la primera vez que vas a usar esa función; queda asociado a tu cuenta con versión{' '}
        <strong>{AI_ASSISTANT_CONSENT_VERSION}</strong>. Los detalles del tratamiento figuran en la
        Política de privacidad.
      </p>
    </LegalPageLayout>
  );
}
